import Vue from 'vue';
import Vuex from 'vuex';
import { vuexfireMutations, firestoreAction } from 'vuexfire';
import { User, Equipment } from '../models';
import {
    auth,
    inventoryCollection,
    messagesCollection,
    uploadsCollection,
    usersCollection,
    tagsCollection,
    statusesCollection,
    categoriesCollection,
    bannersCollection,
    contactsCollection,
    optionsCollection,
    receivedChecklistCollection,
    salesReadyChecklistCollection,
    expensesCollection,
    db,
} from '../db'
import * as firebase from 'firebase/app';
import 'firebase/auth'

export interface RootState {
    user: any | null;
    loggedIn: boolean;
    inventory: Array<Equipment>;
    messages: [];
    uploads: [];
    tags: [];
    statuses: [];
    categories: [];
    options: [];
    banners: [];
    contacts: [];
    users: [];
    checklistReceived: [];
    salesReadyChecklist: [];
    expenses: [];
}

Vue.use(Vuex);

const store = new Vuex.Store<RootState>({
    modules: {},
    state: {
        loggedIn: false,
        user: null,
        inventory: [],
        messages: [],
        uploads: [],
        tags: [],
        statuses: [],
        categories: [],
        options: [],
        banners: [],
        contacts: [],
        users: [],
        checklistReceived: [],
        salesReadyChecklist: [],
        expenses: [],
    },
    actions: {

        async startup({ commit, dispatch }) {
            firebase.auth().onAuthStateChanged(async user => {
                console.log("onAuthStateChanged", user)
                dispatch("fetchUser", user);
            });
            dispatch('bindInventoryRef');
            dispatch('bindBannersRef');
            dispatch('bindCategoriesRef');
            dispatch('bindStatusesRef');
            dispatch('bindOptionsRef');
        },
        async login({ commit }) {

            const provider = new firebase.auth.GoogleAuthProvider();
            provider.addScope('email');
            await auth.signInWithRedirect(provider);
        },
        async logout({ commit, dispatch }) {
            auth.signOut();
            // Clear messages, should only be used with admins
            commit('SET_LOGGED_IN', false);
            commit('SET_USER', null)
            commit('SET_MESSAGES', []);
            commit('SET_UPLOADS', []);
            commit('SET_CUSTOMERS', []);

            // Update inventory,
            dispatch('bindInventoryRef')
        },
        async fetchUser({ commit, dispatch }, user) {
            if (user) {
                const newUser = {
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    email: user.email || user.providerData[0].email,
                    admin: false,
                    id: user.uid,
                }

                // Check if user is admin and update users collection if new
                const userRef = usersCollection.doc(user.uid);
                const userDoc = await userRef.get();
                if (userDoc.exists) {
                    const data = userDoc.data()
                    if (data) {
                        newUser.admin = data.admin
                    }
                }

                // Update user info, keeping admin
                await userRef.update(newUser);

                if (newUser.admin) {
                    // If admin we can bind messages and statuses
                    commit("SET_LOGGED_IN", true);
                    commit("SET_USER", newUser);

                    dispatch('bindMessagesRef');
                    dispatch('bindUploadsRef');
                    dispatch('bindTagsRef');
                    dispatch('bindContactsRef');
                    dispatch('bindUsersRef');
                    dispatch('bindChecklistReceivedRef');
                    dispatch('bindSalesReadyChecklistRef');
                    dispatch('bindExpensesRef');

                    // Rebind inventory for complete listing
                    dispatch('bindInventoryRef');

                } else {
                    // TODO: We should let end users login, but for now, we log them off if not an admin
                    dispatch("logout");
                }

            } else {
                commit("SET_USER", null);
                commit("SET_LOGGED_IN", false);
            }
        },
        bindInventoryRef: firestoreAction(async context => {
            // context contains all original properties like commit, state, etc
            // and adds `bindFirestoreRef` and `unbindFirestoreRef`
            // we return the promise returned by `bindFirestoreRef` that will
            // resolve once data is ready
            context.unbindFirestoreRef('inventory');
            if (context.state.user?.admin) {
                return context.bindFirestoreRef('inventory', inventoryCollection
                    .orderBy('order', 'asc'), { wait: true })
            }

            // const statusVisibleSnapshot = await db.collection('statuses')
            //     .where('visible', '==', true).get();

            //  const statusVisible = statusVisibleSnapshot.docs.map((doc: any) => {
            //      return ;
            //  });

            return context.bindFirestoreRef('inventory', inventoryCollection
                .where('status', '==', db.collection('statuses').doc("icwiUARe4Qdc6RRQBUGy"))
                .orderBy('order', 'asc'),
                { wait: true, maxRefDepth: 1 });
        }),
        bindUploadsRef: firestoreAction(context => {
            context.unbindFirestoreRef('uploads');
            return context.bindFirestoreRef('uploads', uploadsCollection
                .orderBy('created', 'desc'),
                { wait: true })
        }),
        bindMessagesRef: firestoreAction(context => {
            context.unbindFirestoreRef('messages');
            return context.bindFirestoreRef('messages', messagesCollection
                .orderBy('read', 'asc')
                .orderBy('created', 'desc'),
                { wait: true })
        }),
        bindTagsRef: firestoreAction(context => {
            context.unbindFirestoreRef('tags');
            return context.bindFirestoreRef('tags', tagsCollection
                .orderBy('text', 'asc'),
                { wait: true })
        }),
        bindStatusesRef: firestoreAction(context => {
            context.unbindFirestoreRef('statuses');
            return context.bindFirestoreRef('statuses', statusesCollection
                .orderBy('text', 'asc'),
                { wait: true })
        }),
        bindCategoriesRef: firestoreAction(context => {
            context.unbindFirestoreRef('categories');
            return context.bindFirestoreRef('categories', categoriesCollection
                .orderBy('name', 'asc'),
                { wait: true })
        }),
        bindOptionsRef: firestoreAction(context => {
            context.unbindFirestoreRef('options');
            return context.bindFirestoreRef('options', optionsCollection
                .orderBy('name', 'asc'),
                { wait: true })
        }),
        bindBannersRef: firestoreAction(context => {
            context.unbindFirestoreRef('banners');
            return context.bindFirestoreRef('banners', bannersCollection
                .orderBy('name', 'asc'),
                { wait: true })
        }),
        bindContactsRef: firestoreAction(context => {
            context.unbindFirestoreRef('contacts');
            return context.bindFirestoreRef('contacts', contactsCollection
                .orderBy('created', 'asc'),
                { wait: true })
        }),
        bindUsersRef: firestoreAction(async context => {
            context.unbindFirestoreRef('users');
            return context.bindFirestoreRef('users', usersCollection
                .orderBy('displayName', 'asc'),
                { wait: true })

        }),
        bindChecklistReceivedRef: firestoreAction(async context => {
            context.unbindFirestoreRef('checklistReceived');
            return context.bindFirestoreRef('checklistReceived', receivedChecklistCollection
                .orderBy('order', 'asc'),
                { wait: true })
        }),
        bindSalesReadyChecklistRef: firestoreAction(async context => {
            context.unbindFirestoreRef('salesReadyChecklist');
            return context.bindFirestoreRef('salesReadyChecklist', salesReadyChecklistCollection
                .orderBy('order', 'asc'),
                { wait: true })
        }),
        bindExpensesRef: firestoreAction(async context => {
            context.unbindFirestoreRef('expenses');
            return context.bindFirestoreRef('expenses', expensesCollection
                .orderBy('text', 'asc'),
                { wait: true })
        }),
    },
    getters: {
        loggedIn(state): boolean {
            return state.loggedIn;
        },
        admin(state): boolean {
            return state.user?.admin;
        },
        user(state): any {
            return state.user;
        },
        inventory(state): Array<Record<string, any>> {
            return state.inventory;
        },
        uploads(state): Array<Record<string, any>> {
            return state.uploads;
        },
        messages(state): Array<Record<string, any>> {
            return state.messages;
        },
        statuses(state): Array<Record<string, any>> {
            return state.statuses;
        },
        tags(state): Array<Record<string, any>> {
            return state.tags;
        },
        categories(state): Array<Record<string, any>> {
            return state.categories;
        },
        options(state): Array<Record<string, any>> {
            return state.options;
        },
        banners(state): Array<Record<string, any>> {
            return state.banners;
        },
        contacts(state): Array<Record<string, any>> {
            return state.contacts;
        },
        users(state): Array<Record<string, any>> {
            return state.users;
        },
        checklistReceived(state): Array<Record<string, any>> {
            return state.checklistReceived;
        },
        salesReadyChecklist(state): Array<Record<string, any>> {
            return state.salesReadyChecklist;
        },
        expenses(state): Array<Record<string, any>> {
            return state.expenses;
        },
    },
    mutations: {
        SET_LOGGED_IN(state, value) {
            state.loggedIn = value;
        },
        SET_USER(state, data: User) {
            state.user = data;
        },
        SET_MESSAGES(state, data) {
            state.messages = data;
        },
        SET_CUSTOMERS(state, data) {
            state.contacts = data;
        },
        SET_UPLOADS(state, data) {
            state.uploads = data;
        },
        ...vuexfireMutations,
    },
});

export default store;
